import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import {
  getCategoriesAction,
  deleteCategoryAction,
} from "../../../store/actions/CategoryAction";
import swal from "sweetalert";
import Loader from "../Common/Loader";

const Categories = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // Updated to match your store structure
  const categoriesState = useSelector((state) => state.categories || {});
  const { categories = [], loading = false, error = null, num_rows = 0 } = categoriesState;

  const [searchQueries, setSearchQueries] = useState({
    category_name: "",
    category_description: "",
    category_status: "",
  });

  const [sortField, setSortField] = useState("category_name");
  const [sortOrder, setSortOrder] = useState("asc");
  const [currentPage, setCurrentPage] = useState(0);
  const rowsPerPage = 10;
  const [isLoading, setIsLoading] = useState(false);

  // Track active search for server-side filtering
  const [activeSearchField, setActiveSearchField] = useState("");
  const [activeSearchKeyword, setActiveSearchKeyword] = useState("");

  useEffect(() => {
    const fetchCategories = async () => {
      setIsLoading(true);
      try {
        await fetchCategoriesFromServer();
      } catch (err) {
        console.error("Failed to fetch categories:", err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchCategories();
  }, [dispatch]);

  const fetchCategoriesFromServer = async (
    search_by = "",
    search_keyword = "",
    sort_by = sortField,
    sort_order = sortOrder,
    pagination = true,
    pageid = 0
  ) => {
    try {
      await dispatch(
        getCategoriesAction(pagination, pageid, search_by, search_keyword, sort_by, sort_order)
      );
    } catch (err) {
      console.error("Failed to fetch categories:", err);
    }
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    fetchCategoriesFromServer(
      activeSearchField,
      activeSearchKeyword,
      sortField,
      sortOrder,
      true,
      page
    );
  };

  const handleSearch = (e, field) => {
    const keyword = e.target.value;
    
    // Clear all search queries first, then set the current field
    const clearedQueries = {
      category_name: "",
      category_description: "",
      category_status: "",
    };
    
    const updatedQueries = { ...clearedQueries, [field]: keyword };
    setSearchQueries(updatedQueries);
    setCurrentPage(0);

    // Track active search
    setActiveSearchField(field);
    setActiveSearchKeyword(keyword);

    fetchCategoriesFromServer(field, keyword, sortField, sortOrder, true, 0);
  };

  const handleSort = (field) => {
    const order = sortField === field && sortOrder === "asc" ? "desc" : "asc";
    setSortField(field);
    setSortOrder(order);

    fetchCategoriesFromServer(
      activeSearchField,
      activeSearchKeyword,
      field,
      order,
      true,
      currentPage
    );
  };

  const handleEdit = (category) => {
    navigate(`/categories/update-category/${category.category_id}`, {
      state: { category },
    });
  };

  const handleDelete = (category) => {
    swal({
      title: "Are you sure?",
      text: "Once deleted, you will not be able to recover this category!",
      icon: "warning",
      buttons: true,
      dangerMode: true,
    }).then(async (willDelete) => {
      if (willDelete) {
        try {
          setIsLoading(true);
          await dispatch(deleteCategoryAction({ category_id: category.category_id }));
          // Refresh current page after deletion
          fetchCategoriesFromServer(
            activeSearchField,
            activeSearchKeyword,
            sortField,
            sortOrder,
            true,
            currentPage
          );
        } catch (err) {
          console.error("Delete failed:", err);
        } finally {
          setIsLoading(false);
        }
      }
    });
  };

  // Server already returns paginated data
  const paginatedData = categories;
  const totalPages = Math.ceil(num_rows / rowsPerPage);

  if (isLoading) {
    return <Loader />;
  }

  return (
    <>
      <div className="row mb-3">
        <div className="col-12 text-end">
          <Link
            to="/categories/add-categories"
            className="btn btn-primary btn-rounded"
          >
            + Add Categories
          </Link>
        </div>
      </div>

      <div className="row">
        <div className="col-12">
          <div className="card">
            <div className="card-body">
              {error && <div className="text-danger mb-2">{error}</div>}
              {loading ? (
                <div className="text-center my-5">
                  <Loader size="lg" />
                </div>
              ) : (
                <>
                  <div className="table-responsive">
                    <table className="table table-striped mb-4">
                      <thead className="table-light">
                        <tr>
                          <th
                            onClick={() => handleSort("category_name")}
                            style={{ cursor: "pointer" }}
                          >
                            Category Name{" "}
                            {sortField === "category_name" &&
                              (sortOrder === "asc" ? "↑" : "↓")}
                            <input
                              className="form-control form-control-sm mt-1"
                              value={searchQueries.category_name}
                              onChange={(e) => handleSearch(e, "category_name")}
                              onClick={(e) => e.stopPropagation()}
                              placeholder="Search Category Name"
                            />
                          </th>
                          <th
                            onClick={() => handleSort("category_description")}
                            style={{ cursor: "pointer" }}
                          >
                            Description{" "}
                            {sortField === "category_description" &&
                              (sortOrder === "asc" ? "↑" : "↓")}
                            <input
                              className="form-control form-control-sm mt-1"
                              value={searchQueries.category_description}
                              onChange={(e) => handleSearch(e, "category_description")}
                              onClick={(e) => e.stopPropagation()}
                              placeholder="Search Description"
                            />
                          </th>
                          <th
                            onClick={() => handleSort("category_status")}
                            style={{ cursor: "pointer" }}
                          >
                            Status{" "}
                            {sortField === "category_status" &&
                              (sortOrder === "asc" ? "↑" : "↓")}
                            <input
                              className="form-control form-control-sm mt-1"
                              value={searchQueries.category_status}
                              onChange={(e) => handleSearch(e, "category_status")}
                              onClick={(e) => e.stopPropagation()}
                              placeholder="Search Status"
                            />
                          </th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {paginatedData.length > 0 ? (
                          paginatedData.map((category) => (
                            <tr key={category.category_id}>
                              <td>{category.category_name}</td>
                              <td>{category.category_description}</td>
                              <td>
                                <span
                                  className={`badge px-3 py-1 rounded-pill text-white ${
                                    category.category_status.toLowerCase() === "active"
                                      ? "bg-success"
                                      : "bg-danger"
                                  }`}
                                >
                                  {category.category_status}
                                </span>
                              </td>
                              <td className="align-middle">
                                <div className="d-flex">
                                  <span
                                    className="edit me-3"
                                    onClick={() => handleEdit(category)}
                                    style={{ cursor: "pointer" }}
                                    title="Edit Category"
                                  >
                                    <i className="fa fa-pencil-alt text-success fs-18" />
                                  </span>
                                  <span
                                    className="delete "
                                    onClick={() => handleDelete(category)}
                                    style={{ cursor: "pointer" }}
                                    title="Delete Category"
                                  >
                                    <i className="fa fa-trash text-danger fs-18" />
                                  </span>
                                </div>
                              </td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan="4" className="text-center">
                              {loading ? "Loading..." : "No categories found."}
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>

                  {/* Pagination */}
                  {num_rows > 0 && (
                    <div
                      className="pagination-wrapper"
                      style={{
                        display: "flex",
                        gap: "6px",
                        alignItems: "center",
                        justifyContent: "center",
                        marginTop: "20px",
                        flexWrap: "wrap",
                        flexDirection: "column",
                      }}
                    >
                      <div>
                        Showing {categories?.length === 0 ? 0 : currentPage * rowsPerPage + 1} to{" "}
                        {Math.min((currentPage + 1) * rowsPerPage, num_rows)} of {num_rows} entries
                      </div>
                      <div style={{ display: "flex", gap: "6px", flexWrap: "wrap", justifyContent: "center" }}>
                        <button
                          // onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 0))}
                          onClick={() => handlePageChange(Math.max(currentPage - 1, 0))}
                          disabled={currentPage === 0}
                          style={{
                            padding: "6px 12px",
                            border: "1px solid #ddd",
                            borderRadius: "4px",
                            backgroundColor: "#fff",
                            color: "#000",
                            cursor: currentPage === 0 ? "not-allowed" : "pointer",
                            opacity: currentPage === 0 ? 0.5 : 1,
                          }}
                        >
                          &lt;
                        </button>
                        {(() => {
                          const pages = [];
                          const maxPagesToShow = 5;
                          const startPage = Math.max(0, Math.min(currentPage - 2, totalPages - maxPagesToShow));
                          const endPage = Math.min(startPage + maxPagesToShow, totalPages);

                          if (startPage > 0) {
                            pages.push(
                              <button
                                key={0}
                                onClick={() => handlePageChange(0)}
                                style={{
                                  padding: "6px 12px",
                                  border: "1px solid #ddd",
                                  borderRadius: "4px",
                                  backgroundColor: "#fff",
                                  color: "#000",
                                  cursor: "pointer",
                                }}
                              >
                                1
                              </button>
                            );
                            if (startPage > 1) {
                              pages.push(
                                <span key="dots1" style={{ padding: "6px 12px", userSelect: "none" }}>
                                  ...
                                </span>
                              );
                            }
                          }

                          for (let i = startPage; i < endPage; i++) {
                            const isActive = currentPage === i;
                            pages.push(
                              <button
                                key={i}
                                onClick={() => handlePageChange(i)}
                                style={{
                                  padding: "6px 12px",
                                  border: "1px solid #ddd",
                                  borderRadius: "4px",
                                  backgroundColor: isActive ? "#007bff" : "#fff",
                                  color: isActive ? "#fff" : "#000",
                                  cursor: "pointer",
                                }}
                              >
                                {i + 1}
                              </button>
                            );
                          }

                          if (endPage < totalPages) {
                            if (endPage < totalPages - 1) {
                              pages.push(
                                <span key="dots2" style={{ padding: "6px 12px", userSelect: "none" }}>
                                  ...
                                </span>
                              );
                            }
                            pages.push(
                              <button
                                key={totalPages - 1}
                                onClick={() => handlePageChange(totalPages - 1)}
                                style={{
                                  padding: "6px 12px",
                                  border: "1px solid #ddd",
                                  borderRadius: "4px",
                                  backgroundColor: "#fff",
                                  color: "#000",
                                  cursor: "pointer",
                                }}
                              >
                                {totalPages}
                              </button>
                            );
                          }

                          return pages;
                        })()}
                        <button
                          // onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages - 1))}
                          onClick={() => handlePageChange(Math.min(currentPage + 1, totalPages - 1))}
                          disabled={currentPage === totalPages - 1}
                          style={{
                            padding: "6px 12px",
                            border: "1px solid #ddd",
                            borderRadius: "4px",
                            backgroundColor: "#fff",
                            color: "#000",
                            cursor: currentPage === totalPages - 1 ? "not-allowed" : "pointer",
                            opacity: currentPage === totalPages - 1 ? 0.5 : 1,
                          }}
                        >
                          &gt;
                        </button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Categories;
